import { ApiResponse } from "../types";
import { Constants } from "../utils/constants";

class AmenitiesService {
  private baseURL: string;

  constructor() {
    this.baseURL = Constants.API_BASE_URL;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<ApiResponse> {
    const url = `${this.baseURL}${endpoint}`;

    // Get auth token with fallback like apiV2Service
    let token = localStorage.getItem("authToken");
    if (!token) {
      token = localStorage.getItem("access_token");
    }

    const defaultOptions: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(url, { ...defaultOptions, ...options });

    // handle 204 or empty responses
    let data = null;
    const contentType = response.headers.get("Content-Type") || "";
    if (contentType.includes("application/json")) {
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
    }

    return {
      status: response.status,
      data,
      message: data?.message || "",
    };
  }

  // Get all amenities
  async getAllAmenities(sort?: string): Promise<ApiResponse> {
    const sortParam = sort ? `?sort=${sort}` : "?sort=name,asc";
    return this.makeRequest(`/booking/api/v2/amenities${sortParam}`);
  }

  // Get amenities with pagination
  async getAmenitiesWithPagination(pageNumber = 0, pageSize = 10, sort?: string): Promise<ApiResponse> {
    const sortParam = sort ? `sort=${sort}` : "sort=name,asc";
    return this.makeRequest(`/booking/api/v2/amenities?pageNumber=${pageNumber}&pageSize=${pageSize}&${sortParam}`);
  }

  // Create a new amenity
  async createAmenity(amenity: { name: string; description?: string }): Promise<ApiResponse> {
    return this.makeRequest("/booking/api/v2/amenities", {
      method: "POST",
      body: JSON.stringify(amenity),
    });
  }

  // Update an existing amenity
  async updateAmenity(
    id: number,
    amenity: {
      name: string;
      description?: string;
    }
  ): Promise<ApiResponse> {
    return this.makeRequest(`/booking/api/v2/amenities/${id}`, {
      method: "PUT",
      body: JSON.stringify(amenity),
    });
  }

  // Delete an amenity
  async deleteAmenity(id: number): Promise<ApiResponse> {
    return this.makeRequest(`/booking/api/v2/amenities/${id}`, {
      method: "DELETE",
    });
  }

  // Get amenity by ID
  async getAmenityById(id: number): Promise<ApiResponse> {
    return this.makeRequest(`/booking/api/v2/amenities/${id}`);
  }
}

export const amenitiesService = new AmenitiesService();
export default amenitiesService;
